import { useState } from 'react';
import {
  Button,
  Dialog,
  Group,
  IconEdit,
  IconPlusCircle,
  IconTrash,
  Table,
  Typography,
  useModalPortal
} from '@screentone/core';

import { Accordion } from 'components/accordion/Accordion';
import { useAlert } from 'contexts/alert/useAlert';
import { useConvertedProperty } from 'hooks';
import { ModuleParameterSchemaForm } from './ModuleParameterSchemaForm';
import { useModuleParameterSchemas } from './useModuleParameterSchemas';

export type ModuleParameterSchemaEntry = {
  key: string;
  label: string;
  type: string;
  enumValues?: string[] | null;
  deprecated?: boolean | null;
};

interface ModuleParameterSettingsProps {
  isReadOnly?: boolean;
}

const ModuleParameterSettings = ({ isReadOnly = false }: ModuleParameterSettingsProps) => {
  const currentProperty = useConvertedProperty();
  const { alertError, alertSuccess } = useAlert();
  const { renderNode } = useModalPortal();

  const { schemas, error, createSchema, updateSchema, deleteSchema } = useModuleParameterSchemas(currentProperty ?? '');

  const [editingEntry, setEditingEntry] = useState<ModuleParameterSchemaEntry | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleAdd = () => {
    setEditingEntry(null);
    setModalOpen(true);
  };

  const handleEdit = (entry: ModuleParameterSchemaEntry) => {
    setEditingEntry(entry);
    setModalOpen(true);
  };

  const handleDelete = async (key: string) => {
    if (!currentProperty) return;
    try {
      await deleteSchema(key);
      alertSuccess('Parameter deleted.');
    } catch (err) {
      alertError('Failed to delete parameter.');
    }
  };

  const handleFormSave = async (entry: ModuleParameterSchemaEntry) => {
    if (!currentProperty) return;
    try {
      if (editingEntry) {
        await updateSchema(editingEntry.key, entry);
        alertSuccess('Parameter updated.');
      } else {
        await createSchema(entry);
        alertSuccess('Parameter created.');
      }
      setModalOpen(false);
      setEditingEntry(null);
    } catch (err) {
      alertError('Failed to save parameter.');
    }
  };

  const handleFormCancel = () => {
    setModalOpen(false);
    setEditingEntry(null);
  };

  if (error) {
    return (
      <Accordion title="Module Parameters">
        <Typography variant="bodytext" color="asphalt">
          Failed to load module parameter schemas.
        </Typography>
      </Accordion>
    );
  }

  if (Array.isArray(schemas)) {
    return (
      <>
        <Typography variant="bodytext" color="asphalt">
          Define the parameters that can be configured per module. These apply to all modules for this publication.
        </Typography>
        {!isReadOnly && (
          <Group align="end" margin={{ bottom: 'sm' }}>
            <Button tertiary icon={IconPlusCircle} iconPosition="left" onClick={handleAdd}>
              Add Parameter
            </Button>
          </Group>
        )}
        {schemas.length === 0 ? (
          <Typography variant="bodytext" color="asphalt">
            No module parameters defined yet.
          </Typography>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table fullWidth>
              <Table.Head>
                <Table.Row>
                  <Table.Cell>
                    <Typography variant="note" weight="bold">
                      Key
                    </Typography>
                  </Table.Cell>
                  <Table.Cell>
                    <Typography variant="note" weight="bold">
                      Label
                    </Typography>
                  </Table.Cell>
                  <Table.Cell>
                    <Typography variant="note" weight="bold">
                      Type
                    </Typography>
                  </Table.Cell>
                  <Table.Cell>
                    <Typography variant="note" weight="bold">
                      Enum Values
                    </Typography>
                  </Table.Cell>
                  <Table.Cell>
                    <Typography variant="note" weight="bold">
                      Deprecated
                    </Typography>
                  </Table.Cell>
                  {!isReadOnly && (
                    <Table.Cell>
                      <Typography variant="note" weight="bold">
                        Actions
                      </Typography>
                    </Table.Cell>
                  )}
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {schemas.map((schema) => (
                  <Table.Row key={schema.key}>
                    <Table.Cell>
                      <Typography variant="note">{schema.key}</Typography>
                    </Table.Cell>
                    <Table.Cell>{schema.label}</Table.Cell>
                    <Table.Cell>
                      <Typography variant="note">{schema.type}</Typography>
                    </Table.Cell>
                    <Table.Cell>{schema.enumValues?.join(', ') ?? '—'}</Table.Cell>
                    <Table.Cell>
                      {schema.deprecated ? (
                        <Typography variant="note" color="lava">
                          Yes
                        </Typography>
                      ) : (
                        '—'
                      )}
                    </Table.Cell>
                    {!isReadOnly && (
                      <Table.Cell>
                        <Group gap="sm">
                          <Button tertiary componentEl="button" icon={IconEdit} onClick={() => handleEdit(schema)} />
                          <Button
                            tertiary
                            componentEl="button"
                            icon={IconTrash}
                            color="lava"
                            onClick={() => void handleDelete(schema.key)}
                          />
                        </Group>
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}

        <Dialog status={modalOpen ? 'open' : 'closed'} onDismiss={handleFormCancel} renderNode={renderNode}>
          <Dialog.Title>{editingEntry ? 'Edit Parameter' : 'Add Parameter'}</Dialog.Title>
          <Dialog.Content>
            <ModuleParameterSchemaForm
              initial={editingEntry ?? undefined}
              onSave={(entry) => void handleFormSave(entry)}
              onCancel={handleFormCancel}
            />
          </Dialog.Content>
        </Dialog>
      </>
    );
  }

  return (
    <Accordion title="Module Parameters">
      <Typography variant="bodytext">Loading module parameters...</Typography>
    </Accordion>
  );
};

export default ModuleParameterSettings;
